import type { ColumnDef, Row } from '@tanstack/react-table';
import { Camera, ChevronRight, ExternalLink, ImageOff } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Tooltip } from '../ui/tooltip';
import { NS_BASE_URL } from '../../config/external_links';
import { cn } from '../../lib/utils';

/**
 * Widths are fixed so the pinned offsets stay stable across every table.
 *
 * Each number is `content + horizontal padding`, and the padding half of that
 * is not decoration: TanStack computes a pinned column's sticky offset from
 * `size`, so a column whose real rendered width exceeds its declared `size`
 * shifts every column pinned after it. The control columns therefore opt into
 * the `tight` cell padding (`px-1.5`, 6px a side) via `meta` — the review's
 * "the expand and actions columns are too wide" — and the constants below
 * assume it.
 *
 *   select  = 15 (checkbox)                    + 12 = 27 → 44 (kept: room to click)
 *   expand  = 24 (h-6 button)                  + 12 = 36
 *   actions = 92 (3 × 28px icon button, gap-1) + 12 = 104
 *   actions (compact) = 60 (2 buttons)         + 12 = 72 → 76
 */
export const SELECT_COL_WIDTH = 44;
export const EXPAND_COL_WIDTH = 36;
export const ACTIONS_COL_WIDTH = 104;
/** For tables whose row actions are one visible button plus the overflow menu. */
export const ACTIONS_COL_WIDTH_COMPACT = 76;

/** Cell padding for the narrow control columns. Read by `DataTable`. */
export const TIGHT_CELL: { padding: 'tight' } = { padding: 'tight' };

/**
 * Leading checkbox column.
 *
 * `selectableRow` lets a view exclude rows that cannot be bulk-actioned (e.g.
 * already-reviewed rows) — the header checkbox then only ever covers rows the
 * bulk action can actually touch, so the count never over-promises.
 */
export function selectColumn<TData>(
  selectableRow: (row: Row<TData>) => boolean = () => true,
): ColumnDef<TData> {
  return {
    id: 'select',
    size: SELECT_COL_WIDTH,
    enableHiding: false,
    header: ({ table }) => {
      const rows = table.getRowModel().rows.filter(selectableRow);
      const selected = rows.filter(r => r.getIsSelected()).length;
      const checked = rows.length > 0 && selected === rows.length;
      return (
        <Checkbox
          aria-label="Select all rows on this page"
          disabled={rows.length === 0}
          checked={checked ? true : selected > 0 ? 'indeterminate' : false}
          onCheckedChange={value => rows.forEach(r => r.toggleSelected(!!value))}
        />
      );
    },
    cell: ({ row }) =>
      selectableRow(row) ? (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ) : null,
  };
}

/** Expand caret. Detail is fetched by the page when the row opens, never before. */
export function expandColumn<TData>(): ColumnDef<TData> {
  return {
    id: 'expand',
    size: EXPAND_COL_WIDTH,
    enableHiding: false,
    meta: TIGHT_CELL,
    header: () => null,
    cell: ({ row }) => (
      <button
        type="button"
        onClick={row.getToggleExpandedHandler()}
        aria-label={row.getIsExpanded() ? 'Hide details' : 'Show details'}
        aria-expanded={row.getIsExpanded()}
        title="Preview details"
        className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <ChevronRight
          className={cn('h-3.5 w-3.5 transition-transform duration-150', row.getIsExpanded() && 'rotate-90')}
        />
      </button>
    ),
  };
}

/**
 * The NS ID cell — a bare number is not obviously a link, so the tooltip says
 * where it goes (the review asked for exactly this wording). Defined once
 * because three list views render the same cell.
 */
export function NsIdLink({ id }: { id: number | string | null | undefined }) {
  if (id == null || id === '') return <>—</>;
  return (
    <Tooltip label="Open in NetSuite">
      <a
        href={`${NS_BASE_URL}${id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whitespace-nowrap font-mono text-[12.5px] underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-500"
      >
        {id}
      </a>
    </Tooltip>
  );
}

/**
 * Vendor screenshot affordance for an expanded row.
 *
 * Deliberately a link, not a thumbnail. `screenshot_url` is the Google Drive
 * *webViewLink* the scrapers store (`upload_to_drive` returns `webViewLink`) —
 * an HTML viewer page, not image bytes — so an <img> pointing at it can never
 * render and showed a broken tile on every row. Drive's `/thumbnail?id=` URL
 * would render, but it means parsing the file id out of a link whose shape the
 * legacy scrapers own, on an undocumented endpoint that rate-limits.
 */
export function ScreenshotLink({ url }: { url: string | null | undefined }) {
  if (!url) {
    return (
      <div className="flex items-center gap-2 text-[12.5px] text-zinc-400">
        <ImageOff className="h-3.5 w-3.5" />
        No screenshot
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex w-fit items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-[12.5px] text-foreground hover:bg-accent"
    >
      <Camera className="h-3.5 w-3.5 text-muted-foreground" />
      Open screenshot
      <ExternalLink className="h-3 w-3 text-muted-foreground" />
    </a>
  );
}

/**
 * Expanded-row detail panel.
 *
 * Indented past the pinned columns so it visually hangs off its own row, and
 * laid out as titled sections the way the Item Detail page presents the same
 * data — the point of the preview is to avoid the trip to that page.
 */
export function ExpandedPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-6 py-4 pl-[84px] pr-5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_220px]">
      {children}
    </div>
  );
}

export function ExpandedSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-400">{title}</div>
      {children}
    </div>
  );
}

/** Definition list used inside an ExpandedSection. */
export function ExpandedFields({ rows }: { rows: [label: string, value: React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[116px_minmax(0,1fr)] gap-x-3 gap-y-2 text-[12.5px]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words">{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
